import { LargeHeadingStyled } from "../../../components/custom/texts/texts.styled";
import { Button, Card, Drawer, Dropdown, Flex, Form, Icon } from "@nubeteck/components";

import { Input, Layout, Table, Skeleton } from "antd";
import { ColumnsType } from "antd/es/table";
import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { FormInstance } from "antd/lib/form";
import { toastErrorStyle, useSuccessRegisterToast, useErrorFieldsNeedReviewToast, useSuccessStateUpdatedToast } from "src/constants";
import {
  useDeleteSponsorMutation,
  useCreateSponsorMutation,
  useGetSponsorsMutation,
  useUpdateSponsorMutation,
} from "src/services";
import { getSettingsPage } from "../../../Api/content";
import { useLocale } from "../../../i18n/localeContext";
import { StrapiError } from "../../StrapiError";
import { _translateValue } from "../translation-gestor/translateValue";

const { Content } = Layout;
const SCREEN_NAME = "PantallaSponsors";

interface ISponsor {
  patrocinioId: number;
  estadoId: number;
  patrocinioNombre: string;
  codigo: string;
}

const Sponsors: React.FC = () => {
  const [form] = Form.useForm<ISponsor>();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [patrocinioId, setPatrocinioId] = React.useState(0);
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  const { locale } = useLocale();
  const [settingsPage, setSettingsPage] = useState<any>(null);
  const [isLoadingContent, setIsLoadingContent] = useState<boolean>(true);
  const [hasError, setHasError] = useState(false);
  const showSuccessToast = useSuccessRegisterToast();
  const [translatedData, setTranslatedData] = React.useState<{
    patrocinadores: { [key: string]: string };
    estados: { [key: string]: string };
  }>({
    patrocinadores: {},
    estados: {},
  });
  const showErrorFieldsNeedReviewToast = useErrorFieldsNeedReviewToast();
  const showSuccessStateUpdatedToast = useSuccessStateUpdatedToast();

  useEffect(() => {
    let cancel = false;
    setHasError(false);
    setIsLoadingContent(true);
    
    const timeoutId = setTimeout(() => {
      if (!cancel) {
        setSettingsPage(null);
        setIsLoadingContent(false);
        setHasError(locale === 'en');
      }
    }, 3000);
    
    getSettingsPage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          clearTimeout(timeoutId);
          setSettingsPage(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => {
        if (!cancel) {
          clearTimeout(timeoutId);
          setSettingsPage(null);
          setIsLoadingContent(false);
          setHasError(locale === 'en');
        }
      });
    return () => {
      cancel = true;
      clearTimeout(timeoutId);
    };
  }, [locale]);

  const formRef = React.useRef<FormInstance>(null);
  const { resetFields, setFieldsValue, validateFields } = form;
  const [getSponsors, { data, isLoading }] = useGetSponsorsMutation();
  const [
    createSponsor,
    { isLoading: isLoadingCreate, isSuccess: isSuccessCreate, error: errorCreate },
  ] = useCreateSponsorMutation();
  const [
    updateTemplate,
    { isLoading: isLoadingUpdate, isSuccess: isSuccessUpdate, error: errorUpdate },
  ] = useUpdateSponsorMutation();
  const [
    changeSponsorState,
    { isLoading: isLoadingState, isSuccess: isSuccessState, error: errorState },
  ] = useDeleteSponsorMutation();

  React.useEffect(() => {
    getSponsors();
  }, [getSponsors, isSuccessCreate, isSuccessUpdate, errorCreate, errorUpdate, isSuccessState]);

  React.useEffect(() => {
    setDrawerOpen(false);
    if (errorCreate) toast.error(`Error: ${errorCreate.data?.message}`, toastErrorStyle);

    if (isSuccessCreate) {
      showSuccessToast();
      resetFields();
      setPatrocinioId(0);
    }
  }, [isSuccessCreate, resetFields, errorCreate, showSuccessToast]);

  React.useEffect(() => {
    setDrawerOpen(false);
    if (errorUpdate) toast.error(`Error: ${errorUpdate.data?.message}`, toastErrorStyle);

    if (isSuccessUpdate) {
      showSuccessToast();
      resetFields();
      setPatrocinioId(0);
    }
  }, [isSuccessUpdate, resetFields, errorUpdate, showSuccessToast]);

  React.useEffect(() => {
    if (errorState) {
      toast.error(`Error: ${errorState.data?.message}`, toastErrorStyle);
    }

    if (isSuccessState) {
      showSuccessStateUpdatedToast();
    }
  }, [errorState, isSuccessState, showSuccessStateUpdatedToast]);

  // Efecto para traducir los campos traducibles cuando lleguen los datos o cambie el locale
  useEffect(() => {
    const translateData = async () => {
      const hasData = data?.data && Array.isArray(data.data) && data.data.length > 0;
      if (!hasData) {
        setTranslatedData({ patrocinadores: {}, estados: {} });
        return;
      }

      const patrocinadoresTranslations: { [key: string]: string | null } = {};
      const estadosTranslations: { [key: string]: string | null } = {};
      const translationPromises: Promise<void>[] = [];

      data.data.forEach((record: ISponsor) => {
        if (record.patrocinioNombre && !patrocinadoresTranslations.hasOwnProperty(record.patrocinioNombre)) {
          patrocinadoresTranslations[record.patrocinioNombre] = null;
          translationPromises.push(
            _translateValue(SCREEN_NAME, record.patrocinioNombre, locale).then((translated) => {
              patrocinadoresTranslations[record.patrocinioNombre] = translated;
            })
          );
        }
        const estadoText = record.estadoId == 11 ? "Activo" : "Inactivo";
        if (!estadosTranslations.hasOwnProperty(estadoText)) {
          estadosTranslations[estadoText] = null;
          translationPromises.push(
            _translateValue(SCREEN_NAME, estadoText, locale).then((translated) => {
              estadosTranslations[estadoText] = translated;
            })
          );
        }
      });

      await Promise.all(translationPromises);
      setTranslatedData({
        patrocinadores: patrocinadoresTranslations as { [key: string]: string },
        estados: estadosTranslations as { [key: string]: string },
      });
    };
    translateData();
  }, [data?.data, locale]);

  const onClickEditarPatrocinador = React.useCallback(
    (record: ISponsor) => {
      setPatrocinioId(record.patrocinioId);
      setFieldsValue(record);

      setDrawerOpen(true);
    },
    [setFieldsValue],
  );

  const cambiarEstadoPatrocinador = React.useCallback(
    (value: number, id: number) => {
      changeSponsorState({ estadoId: value === 11 ? 12 : 11, id });
    },
    [changeSponsorState],
  );

  const submit = () => {
    validateFields(["patrocinioNombre", "codigo"])
      .then((values) => {
        const v = {
          ...values,
        };
        if (patrocinioId !== 0) {
          updateTemplate({
            ...v,
            id: patrocinioId,
          });
        } else {
          createSponsor(v);
        }
      })
      .catch(() => {
        showErrorFieldsNeedReviewToast();
      });
  };

  const columns = React.useMemo<ColumnsType<ISponsor>>(
    () => [
      {
        title: settingsPage?.sponsor_setting_table?.[0]?.label || "Código",
        dataIndex: "codigo",
      },
      {
        title: settingsPage?.sponsor_setting_table?.[1]?.label || "Patrocinador",
        dataIndex: "patrocinioNombre",
        render: (text: string) => translatedData.patrocinadores[text] || text,
      },
      {
        title: settingsPage?.sponsor_setting_table?.[2]?.label || "Estado",
        dataIndex: "estadoId",
        render: (_: number, record: ISponsor) => {
          const estadoText = record.estadoId == 11 ? "Activo" : "Inactivo";
          return translatedData.estados[estadoText] || estadoText;
        },
      },
      {
        title: "",
        dataIndex: "actions",
        fixed: "right",
        width: 100,
        render: (_, record) => (
          <Dropdown
            overlayClassName="table-dropdown"
            className="cursor-pointer"
            key={record.patrocinioId}
            trigger={["click"]}
            align={{ offset: [-40, 4] }}
            menu={{
              items: [
                {
                  key: "1",
                  label: settingsPage?.edit_button || "Editar",
                  icon: <Icon name="" color="#A7A7A7" />,
                  onClick: () => onClickEditarPatrocinador(record),
                },
                {
                  key: "2",
                  label: record.estadoId === 11 ? (settingsPage?.inactive_button || "Inactivar") : (settingsPage?.active_button || "Activar"),
                  icon: <Icon name={record.estadoId === 11 ? "" : ""} color="#A7A7A7" />,
                  onClick: () => cambiarEstadoPatrocinador(record.estadoId, record.patrocinioId),
                },
              ],
            }}
          >
            <Icon name="" isTouchable />
          </Dropdown>
        ),
      },
    ],
    [onClickEditarPatrocinador, cambiarEstadoPatrocinador, settingsPage, translatedData],
  );

  if (hasError && locale === 'en') {
    return <StrapiError
      onRetry={() => {
        setHasError(false);
        setIsLoadingContent(true);
      }}
      onUseDefault={() => {
        setHasError(false);
        setIsLoadingContent(false);
        setSettingsPage(null);
      }}
    />;
  }

  return (
    <Flex style={{ flexDirection: "column", width: "100%", alignSelf: "flex-start" }}>
      <Content style={{ marginTop: "16px" }}>
        {isLoadingContent ? (
          <>
            <Flex style={{ width: "100%", justifyContent: "space-between", marginBottom: 16 }}>
              <Skeleton.Input active size="large" style={{ width: 200 }} />
              <Skeleton.Input active size="default" style={{ width: 150 }} />
            </Flex>
            <Card bodyStyle={{ padding: 0, width: "100%" }}>
              <Skeleton active paragraph={{ rows: 6 }} />
            </Card>
          </>
        ) : (
          <>
            <Flex style={{ width: "100%", justifyContent: "space-between" }}>
              <LargeHeadingStyled>{settingsPage?.sponsor__nb || "Patrocinadores"}</LargeHeadingStyled>

              <Button
                title=""
                type="primary"
                loading={false}
                onClick={() => {
                  setDrawerOpen(true);
                  resetFields();
                }}
              >
                {settingsPage?.sponsor_detail?.new_sponsor_button || "Nuevo patrocinador"}
              </Button>
            </Flex>
            <Card bodyStyle={{ padding: 0, width: "100%" }}>
              <Table<ISponsor>
                loading={isLoading || isLoadingCreate || isLoadingUpdate || isLoadingState}
                columns={columns}
                dataSource={data?.data}
                rowKey={(v) => v.patrocinioId}
                pagination={{
                  defaultPageSize: 5,
                  showSizeChanger: true,
                  style: { marginRight: "30px" },
                }}
                scroll={{ x: 800 }}
              />
              <Drawer
                placement="right"
                title={patrocinioId !== 0 ? (settingsPage?.sponsor_detail?.title || "Edición de patrocinador") : (settingsPage?.sponsor_detail?.new_sponsor_button || "Nuevo patrocinador")}
                open={drawerOpen}
                onClose={() => {
                  setPatrocinioId(0);
                  setDrawerOpen(false);
                  resetFields();
                }}
              >
                {" "}
                <Form
                  form={form}
                  layout="vertical"
                  ref={formRef}
                  style={{ display: "flex", flexDirection: "column", gap: 16 }}
                  initialValues={{
                    id: 1,
                  }}
                >
                  <Form.Item
                    name="codigo"
                    label={settingsPage?.sponsor_detail?.code || "Código del patrocinador"}
                    hasFeedback
                    required
                    rules={[
                      {
                        required: true,
                        message: settingsPage?.required_field_message || "Este campo es requerido.",
                      },
                    ]}
                  >
                    <Input type="text" />
                  </Form.Item>
                  <Form.Item
                    name="patrocinioNombre"
                    label={settingsPage?.sponsor_detail?.name || "Nombre del patrocinador"}
                    hasFeedback
                    required
                    rules={[
                      {
                        required: true,
                        message: settingsPage?.required_field_message || "Este campo es requerido.",
                      },
                    ]}
                  >
                    <Input type="text" />
                  </Form.Item>

                  <Button
                    onClick={() => submit()}
                    type="primary"
                    size="large"
                    htmlType="submit"
                    style={{ alignSelf: "flex-end" }}
                    loading={isLoadingCreate || isLoadingState || isLoadingUpdate}
                  >
                    {settingsPage?.sponsor_detail?.save_button || "Guardar"}
                  </Button>
                </Form>
              </Drawer>
            </Card>
          </>
        )}
      </Content>
    </Flex>
  );
};

export default Sponsors;
